import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import moment from 'moment';
//icon
import { AiOutlineEye } from 'react-icons/ai';
// api
import { getTypeSchedulesNotPaginated } from 'api/company';
// helpers
import { verificateNullValue } from 'helpers';
// components
import Select from 'components/backoffice/selects/SelectCustom';
import CustomInput from 'components/backoffice/customInput';
import Button from 'components/backoffice/buttonCustom';
import CkEditorComponent from 'components/backoffice/inputEditor/InputEditorComponent';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import WatchModal from 'components/backoffice/modals/WatchModal';
import Textarea from 'components/backoffice/customInput/textAreaCustom';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const ScheduleForm = ({
  isEditing,
  onSubmitForm,
  fileInputRef,
  onDelete,
  openModalDelete,
  setOpenModalDelete,
  defaultValues,
  beforeTitle,
  loading,
}) => {
  const { t } = useTranslation(['backoffice']);
  const [typesSchedules, setTypesSchedules] = useState([]);
  const [openModalImage, setOpenModalImage] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const {
    formState: { errors },
    setValue,
    watch,
    control,
    getValues,
    handleSubmit,
  } = useForm();
  const imageValue = watch('image');

  const getImageForModal = () => {
    if (previewImage) return previewImage;
    if (typeof imageValue === 'string') return imageValue;
    if (imageValue instanceof Blob || imageValue instanceof File) {
      return URL.createObjectURL(imageValue);
    }
    return '';
  };

  const watchItem = () => {
    setOpenModalImage(true);
  };

  const status = [
    { id: 'draft', value: t('draft') },
    { id: 'published', value: t('published') },
    { id: 'hidden', value: t('hidden') },
  ];

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  let item;
  const image = getValues('image');

  if (isEditing) {
    item =
      typeof image === 'string'
        ? image
        : image instanceof Blob || image instanceof File
          ? URL.createObjectURL(image)
          : '';
  } else {
    item = image !== undefined ? URL.createObjectURL(image) : '';
  }

  useEffect(() => {
    const typeSchedules = async () => {
      try {
        const { data } = await getTypeSchedulesNotPaginated();
        if (data) {
          const newData = data.map((val, i) => {
            return { id: val.id, value: val['name'] };
          });
          setTypesSchedules(newData);
        }
      } catch (error) {
        console.log(error);
      }
    };
    typeSchedules();
  }, []);

  const hasValidDefaultValues = (values) => {
    return Object.values(values || {}).some(
      (v) => v !== null && v !== undefined && v !== '' && !(typeof v === 'object' && Object.keys(v || {}).length === 0),
    );
  };

  useEffect(() => {
    if (!hasValidDefaultValues(defaultValues)) return;
    if (isEditing) {
      setValue('image', null);
      if (defaultValues?.image) {
        fetch(defaultValues.image)
          .then((response) => response.blob())
          .then((blob) => {
            const name = defaultValues.image.split('?')[0].split('/').pop();
            const file = new File([blob], name, { type: `image/${name.split('.')[1]}` });
            setValue('image', file);
          })
          .catch((error) => {
            console.error('Error al obtener la imagen:', error);
          });
      }
    } else {
      setValue('image', defaultValues.image);
    }
    setValue('title', verificateNullValue(defaultValues.title));
    setValue('type_schedule', defaultValues.type_schedule?.id ?? null);
    setValue('status', defaultValues.status);
    setValue('start_date', defaultValues.start_date);
    setValue('end_date', defaultValues.end_date);
    setValue('body', defaultValues.body);
    setValue('tags', defaultValues.tag_list?.join(', ') ?? '');
    setValue('place', verificateNullValue(defaultValues.place));
  }, [defaultValues]);

  return (
    <div className="mt-5">
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="title"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={t('titulo')}
                  onChange={(e) => onChange(e)}
                  placeholder={t('titulo')}
                />
              )}
            />
            {errors?.title && <small className="text-danger">{errors.title.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="type_schedule"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <Select
                  label={t('typeSchedules')}
                  placeholder={!isEditing && t('selectoption')}
                  choices={typesSchedules}
                  onCheckedItem={onChange}
                  value={value}
                />
              )}
            />
            {errors?.type_schedule && <small className="text-danger ">{errors.type_schedule.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Controller
              name="status"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  label={t('state')}
                  placeholder={t('selectoption')}
                  choices={status}
                  onCheckedItem={onChange}
                  value={value}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Form.Label className="text-light">{t('startdate')}</Form.Label>
            <Controller
              name="start_date"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field }) => (
                <DatePicker
                  placeholderText={t('startdate')}
                  className="input-control w-100 form-control"
                  selected={field.value ? new Date(field.value) : null}
                  onChange={(date) => setValue('start_date', moment(date).format('YYYY-MM-DDTHH:mm:ss'))}
                  dateFormat="yyyy-MM-dd"
                />
              )}
            />
            {errors?.start_date && <small className="text-danger ">{errors.start_date.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Form.Label className="text-light">{t('enddate')}</Form.Label>
            <Controller
              name="end_date"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field }) => (
                <DatePicker
                  placeholderText={t('enddate')}
                  className="input-control w-100 form-control"
                  selected={field.value ? new Date(field.value) : null}
                  onChange={(date) => setValue('end_date', moment(date).format('YYYY-MM-DDTHH:mm:ss'))}
                  dateFormat="yyyy-MM-dd"
                />
              )}
            />
            {errors?.end_date && <small className="text-danger ">{errors.end_date.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0 d-flex gap-3">
            <div className="flex-column w-100">
              <Controller
                name="image"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('camporequerido'),
                  },
                }}
                render={({ field: { onChange, value } }) => {
                  return (
                    <CustomInput
                      type="file"
                      dropFileMsg={t('dropFile')}
                      label={t('imagen')}
                      placeholder={t('selectImg')}
                      accept="image/*"
                      value={value}
                      onChange={(e) => {
                        const file = e instanceof File ? e : e?.target?.files?.[0];
                        if (file) {
                          setPreviewImage(URL.createObjectURL(file));
                        } else {
                          setPreviewImage(null);
                        }
                        onChange(e);
                      }}
                      ref={fileInputRef}
                    />
                  );
                }}
              />
              {errors?.image && <small className="text-danger  m-1">{errors.image.message}</small>}
            </div>
            <div className="mt-4-3">
              <Button type="button" color="terciary" onClick={watchItem}>
                <AiOutlineEye color="white" />
              </Button>
            </div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="place"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  value={value}
                  type="text"
                  label={`${t('place')}`}
                  onChange={(e) => onChange(e)}
                  placeholder={t('place')}
                />
              )}
            />
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0">
            <Form.Label className="text-light">{t('tags')}</Form.Label>
            <Controller
              name="tags"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <Textarea value={value} onChange={(e) => onChange(e)} placeholder={t('tags')} />
              )}
            />
            {errors?.tags && <small className="text-danger m-1">{errors.tags.message}</small>}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} className="mb-md-2 mb-lg-0">
            <Form.Label className="text-light">Body</Form.Label>
            <Controller
              name="body"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CkEditorComponent value={value} onChange={(content) => onChange(content)} />
              )}
            />
          </Col>
        </Row>
      </Form>
      <div className="my-4 d-flex justify-content-end gap-3">
        <Button color="primary" onClick={handleSubmit(onSubmitForm)} loading={loading}>
          {t('save')}
          <SpinnerComponent loading={loading} />
        </Button>
        {isEditing && (
          <Button color="secondary" onClick={() => setOpenModalDelete(true)} loading={loading}>
            {t('eliminar')}
          </Button>
        )}
      </div>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={onDelete}
        deleteTitle={t('deleteschedule')}
        beforeTitle={t('edit', { title: defaultValues?.title })}
      />
      <WatchModal
        item={getImageForModal()}
        title={t('imagen')}
        show={openModalImage}
        setShow={setOpenModalImage}
        beforeTitle={defaultValues ? t('edit', { title: defaultValues?.title }) : beforeTitle}
      />
    </div>
  );
};

ScheduleForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  openModalDelete: PropTypes.bool,
  setOpenModalDelete: PropTypes.func,
  onDelete: PropTypes.func,
  id: PropTypes.any,
  beforeTitle: PropTypes.string,
  loading: PropTypes.bool,
};

export default ScheduleForm;